import {
 useState, useMemo, useEffect, useCallback 
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  Divider,
  useTheme,
  alpha,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Skeleton,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MapIcon from '@mui/icons-material/Map';
import TimelineIcon from '@mui/icons-material/Timeline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import RouteIcon from '@mui/icons-material/Route';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlaceIcon from '@mui/icons-material/Place';
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import { useSelector } from 'react-redux';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from '../settings/components/SettingsMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import EventIcon from '@mui/icons-material/Event';
import {
  PremiumActivityFeed,
  StatCard,
  StatCardGrid,
  StatusDistribution,
  DashboardWidget,
  PremiumBarChart,
  PremiumLineChart,
  FadeIn,
  SlideUp,
  AnimatedCounter,
} from '../common/components/premium';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  kpiGrid: {
    marginBottom: theme.spacing(3),
  },
  kpiCard: {
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
    },
  },
  kpiCardOnline: {
    '&::before': {
      backgroundColor: theme.palette.success.main,
    },
  },
  kpiCardOffline: {
    '&::before': {
      backgroundColor: theme.palette.error.main,
    },
  },
  kpiCardMoving: {
    '&::before': {
      backgroundColor: theme.palette.info.main,
    },
  },
  kpiCardAlerts: {
    '&::before': {
      backgroundColor: theme.palette.warning.main,
    },
  },
  kpiValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  kpiTrend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
  kpiIcon: {
    position: 'absolute',
    right: 16,
    top: 16,
    opacity: 0.15,
    fontSize: 64,
  },
  tableCard: {
    marginBottom: theme.spacing(3),
  },
  tableToolbar: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableFilters: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  statusChip: {
    fontWeight: 600,
  },
  deviceRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
  deviceAvatar: {
    width: 40,
    height: 40,
  },
  chartCard: {
    height: 350,
  },
  mapContainer: {
    height: 250,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
  },
  lastUpdate: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  pulsingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.7)}`,
    },
    '70%': {
      boxShadow: `0 0 0 10px ${alpha(theme.palette.success.main, 0)}`,
    },
    '100%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}`,
    },
  },
  gaugeContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  gaugeValue: {
    position: 'absolute',
    textAlign: 'center',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  metricCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  rotating: {
    animation: '$rotate 1s linear infinite',
  },
  '@keyframes rotate': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

const POLLING_INTERVAL = 30000;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const CATEGORY_ICONS = {
  car: DirectionsCarIcon,
  truck: LocalShippingIcon,
  motorcycle: TwoWheelerIcon,
  bus: AirportShuttleIcon,
  tractor: AgricultureIcon,
  default: DirectionsCarIcon,
};

const FleetDashboardPage = () => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const t = useTranslation();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // Premium colors with dark/light mode support
  const isDark = tokens?.isDark !== false;
  const surfaceBase = tokens?.surface?.base || (isDark ? '#0d1117' : '#f6f8fa');
  const surfaceElevated = tokens?.surface?.elevated || (isDark ? '#161b22' : '#ffffff');
  const textPrimary = tokens?.text?.primary || (isDark ? '#f0f6fc' : '#1f2328');
  const textSecondary = tokens?.text?.secondary || (isDark ? '#8b949e' : '#656d76');
  const borderSubtle = tokens?.border?.subtle || (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)');

  // Premium status colors
  const statusColors = {
    online: tokens?.status?.online || '#00d4aa',
    moving: tokens?.status?.moving || '#58a6ff',
    idle: tokens?.status?.idle || '#d29922',
    offline: tokens?.status?.offline || '#6e7681',
    alarm: tokens?.status?.alarm || '#f85149',
  };

  // Redux stores - drivers indexed by uniqueId, groups indexed by id
  const drivers = useSelector((state) => state.drivers.items);
  const groups = useSelector((state) => state.groups.items);

  // State
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [tab, setTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Historical data
  const [historicalData, setHistoricalData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Group filter
  const [groupFilter, setGroupFilter] = useState('all');

  // Events for selected device
  const [deviceEvents, setDeviceEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      else setRefreshing(true);

      const [devicesRes, positionsRes] = await Promise.all([
        fetch('/api/devices'),
        fetch('/api/positions'),
      ]);

      if (devicesRes.ok && positionsRes.ok) {
        const devicesData = await devicesRes.json();
        const positionsData = await positionsRes.json();

        setDevices(devicesData);
        const posMap = {};
        positionsData.forEach((pos) => {
          posMap[pos.deviceId] = pos;
        });
        setPositions(posMap);
        setLastUpdate(new Date());
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch historical positions for a device
  const fetchHistoricalData = useCallback(async (deviceId) => {
    if (!deviceId) return;

    setLoadingHistory(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 6 * 60 * 60 * 1000); // Last 6 hours

      const response = await fetch(
        `/api/positions?deviceId=${deviceId}&from=${from.toISOString()}&to=${to.toISOString()}`
      );

      if (response.ok) {
        const data = await response.json();
        const chartData = data.map((pos) => ({
          time: new Date(pos.fixTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          speed: Math.round((pos.speed || 0) * 1.852),
          timestamp: new Date(pos.fixTime).getTime(),
        })).sort((a, b) => a.timestamp - b.timestamp);

        setHistoricalData(chartData);
      }
    } catch (error) {
      console.error('Error fetching historical data:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Fetch events for a device
  const fetchDeviceEvents = useCallback(async (deviceId, deviceName) => {
    if (!deviceId) return;

    setLoadingEvents(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000); // Last 24 hours

      const response = await fetch(
        `/api/reports/events?deviceId=${deviceId}&from=${from.toISOString()}&to=${to.toISOString()}`,
        { headers: { Accept: 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        // Adiciona deviceName aos eventos para o PremiumActivityFeed
        const eventsWithName = data.slice(0, 20).map((event) => ({
          ...event,
          deviceName: deviceName || `Device ${deviceId}`,
        }));
        setDeviceEvents(eventsWithName);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  // Initial load
  useEffectAsync(async () => {
    await fetchData(true);
  }, []);

  // Polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch history and events when device is selected
  useEffect(() => {
    if (selectedDevice && tab === 1) {
      fetchHistoricalData(selectedDevice.id);
      fetchDeviceEvents(selectedDevice.id, selectedDevice.name);
    }
  }, [selectedDevice, tab, fetchHistoricalData, fetchDeviceEvents]);

  // KPIs calculation
  const kpis = useMemo(() => {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    let online = 0;
    let offline = 0;
    let moving = 0;
    let alerts = 0;
    let totalSpeed = 0;
    let speedCount = 0;
    let totalDistanceKm = 0;

    devices.forEach((device) => {
      const pos = positions[device.id];
      const lastTime = pos ? new Date(pos.fixTime).getTime() : 0;
      const isOnline = now - lastTime < fiveMinutes;

      if (isOnline) {
        online++;
        if (pos?.speed > 0) {
          moving++;
          totalSpeed += pos.speed * 1.852;
          speedCount++;
        }
      } else {
        offline++;
      }

      if (pos?.attributes) {
        if (pos.attributes.alarm) alerts++;
        if (pos.attributes.batteryLevel < 20) alerts++;
        // Sum total distance from all devices (in meters, convert to km)
        if (pos.attributes.totalDistance) {
          totalDistanceKm += pos.attributes.totalDistance / 1000;
        }
      }
    });

    const avgSpeed = speedCount > 0 ? Math.round(totalSpeed / speedCount) : 0;

    return { total: devices.length, online, offline, moving, alerts, avgSpeed, totalDistanceKm: Math.round(totalDistanceKm) };
  }, [devices, positions]);

  // Status distribution for premium StatusDistribution component
  const statusDistributionData = useMemo(() => [
    {
      status: 'online',
      label: 'Online',
      count: kpis.online,
      color: statusColors.online,
    },
    {
      status: 'offline',
      label: 'Offline',
      count: kpis.offline,
      color: statusColors.offline,
    },
  ], [kpis, statusColors]);

  // Category distribution for bar chart
  const categoryDistribution = useMemo(() => {
    const categories = {};
    devices.forEach((device) => {
      const cat = device.category || 'outros';
      categories[cat] = (categories[cat] || 0) + 1;
    });
    return Object.entries(categories).map(([name, count]) => ({ name, count }));
  }, [devices]);

  // Speed distribution for analytics
  const speedDistribution = useMemo(() => {
    const ranges = { '0': 0, '1-30': 0, '31-60': 0, '61-80': 0, '81-100': 0, '100+': 0 };

    devices.forEach((device) => {
      const pos = positions[device.id];
      const speed = Math.round((pos?.speed || 0) * 1.852);

      if (speed === 0) ranges['0']++;
      else if (speed <= 30) ranges['1-30']++;
      else if (speed <= 60) ranges['31-60']++;
      else if (speed <= 80) ranges['61-80']++;
      else if (speed <= 100) ranges['81-100']++;
      else ranges['100+']++;
    });

    return Object.entries(ranges).map(([range, count]) => ({
      range: `${range} km/h`,
      count,
    }));
  }, [devices, positions]);

  // Available groups for filter dropdown
  const availableGroups = useMemo(() => {
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  // Filtered and sorted devices
  const filteredDevices = useMemo(() => {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    return devices
      .map((device) => {
        const pos = positions[device.id];
        const lastTime = pos ? new Date(pos.fixTime).getTime() : 0;
        const isOnline = now - lastTime < fiveMinutes;
        const isMoving = pos?.speed > 0;

        // Get driver from Redux (drivers indexed by uniqueId)
        const driverUniqueId = pos?.attributes?.driverUniqueId;
        const driver = driverUniqueId ? drivers[driverUniqueId] : null;

        // Get group from Redux (groups indexed by id)
        const group = device.groupId ? groups[device.groupId] : null;

        // Get ignition status
        const ignition = pos?.attributes?.ignition;

        // Get total distance
        const totalDistance = pos?.attributes?.totalDistance;

        return {
          ...device,
          position: pos,
          isOnline,
          isMoving,
          speed: pos?.speed || 0,
          address: pos?.address || '-',
          battery: pos?.attributes?.batteryLevel,
          temperature: pos?.attributes?.temperature || pos?.attributes?.temp1,
          lastUpdate: pos?.fixTime || device.lastUpdate,
          latitude: pos?.latitude,
          longitude: pos?.longitude,
          driver,
          driverName: driver?.name || '-',
          group,
          groupName: group?.name || '-',
          ignition,
          totalDistance,
        };
      })
      .filter((device) => {
        if (search && !device.name.toLowerCase().includes(search.toLowerCase())) {
          return false;
        }
        if (statusFilter === 'online' && !device.isOnline) return false;
        if (statusFilter === 'offline' && device.isOnline) return false;
        if (statusFilter === 'moving' && !device.isMoving) return false;
        // Group filter
        if (groupFilter !== 'all' && device.groupId !== parseInt(groupFilter, 10)) return false;
        return true;
      })
      .sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (order === 'asc') {
          return aValue < bValue ? -1 : 1;
        }
        return aValue > bValue ? -1 : 1;
      });
  }, [devices, positions, search, statusFilter, orderBy, order, drivers, groups, groupFilter]);

  // Paginated devices
  const paginatedDevices = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDevices.slice(start, start + rowsPerPage);
  }, [filteredDevices, page, rowsPerPage]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setTab(1);
  };

  const handleEventClick = (event) => {
    if (event.deviceId) {
      // Navegar para /eventmap com período de 24h centrado no evento
      const eventTime = new Date(event.eventTime || event.serverTime);
      const from = new Date(eventTime.getTime() - 12 * 60 * 60 * 1000).toISOString(); // 12h antes
      const to = new Date(eventTime.getTime() + 12 * 60 * 60 * 1000).toISOString(); // 12h depois
      navigate(`/eventmap?deviceId=${event.deviceId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
    }
  };

  const handleMenuOpen = (event, device) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuDevice(device);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuDevice(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Nome', 'Motorista', 'Grupo', 'Status', 'Ignição', 'Velocidade (km/h)', 'Temperatura', 'Bateria', 'Endereço', 'Última Atualização'];
    const rows = filteredDevices.map((d) => [
      d.name,
      d.driverName,
      d.groupName,
      d.isOnline ? (d.isMoving ? 'Movimento' : 'Parado') : 'Offline',
      d.ignition === true ? 'Ligada' : d.ignition === false ? 'Desligada' : '-',
      Math.round((d.speed || 0) * 1.852),
      d.temperature?.toFixed(1) || '-',
      d.battery || '-',
      `"${d.address}"`,
      new Date(d.lastUpdate).toLocaleString(),
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `frota_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
    return <Icon />;
  };

  const formatSpeed = (speed) => {
    return `${Math.round((speed || 0) * 1.852)} km/h`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // KpiCard removido - usando StatCard premium

  // Speed Gauge Component
  const SpeedGauge = ({ value, max = 180 }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const color = value > 100 ? theme.palette.error.main :
                  value > 80 ? theme.palette.warning.main :
                  theme.palette.success.main;

    return (
      <Box sx={{ position: 'relative', width: 200, height: 200, mx: 'auto' }}>
        <ResponsiveContainer width={200} height={200}>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="100%"
            startAngle={180}
            endAngle={0}
            data={[{ value: percentage, fill: color }]}
          >
            <RadialBar
              background
              dataKey="value"
              cornerRadius={10}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <Box className={classes.gaugeValue}>
          <Typography variant="h3" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">km/h</Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'fleetDashboard']}>
        <Box sx={{ p: 4 }}>
          <LinearProgress />
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Skeleton variant="rectangular" height={120} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'fleetDashboard']}>
      <Box className={classes.root} sx={{ bgcolor: surfaceBase }}>
        {/* Header */}
        <FadeIn>
          <Box className={classes.header}>
            <Box className={classes.headerTitle}>
              <Typography variant="h4" fontWeight={700} sx={{ color: textPrimary, background: 'transparent' }}>
                {t('fleetDashboard') || 'Gestão de Frotas'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box className={classes.pulsingDot} sx={{ bgcolor: statusColors.online }} />
                <Typography variant="body2" sx={{ color: textSecondary, background: 'transparent' }}>
                  {t('sharedRealTime') || 'Tempo real'}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.headerActions}>
              {lastUpdate && (
                <Chip
                  size="small"
                  label={`${t('sharedLastUpdate') || 'Atualizado'}: ${lastUpdate.toLocaleTimeString()}`}
                  sx={{
                    bgcolor: surfaceElevated,
                    color: textSecondary,
                    border: `1px solid ${borderSubtle}`,
                    '& .MuiChip-label': { background: 'transparent' },
                  }}
                />
              )}
              <Tooltip title={t('sharedRefresh') || 'Atualizar'}>
                <IconButton onClick={() => fetchData(false)} disabled={refreshing} sx={{ color: textPrimary }}>
                  <RefreshIcon className={refreshing ? classes.rotating : ''} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('sharedExport') || 'Exportar CSV'}>
                <IconButton onClick={handleExportCSV} sx={{ color: textPrimary }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? t('sharedExitFullscreen') || 'Sair da tela cheia' : t('sharedFullscreen') || 'Tela cheia'}>
                <IconButton onClick={handleFullscreen} sx={{ color: textPrimary }}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </FadeIn>

        {/* KPIs - Premium StatCards */}
        <SlideUp delay={100}>
          <StatCardGrid columns={6} gap={2} sx={{ mb: 3 }}>
            <StatCard
              title={t('sharedTotal') || 'Total'}
              value={<AnimatedCounter value={kpis.total} />}
              icon={<LocalShippingIcon />}
              color="primary"
              subtitle={`${kpis.total} dispositivos`}
            />
            <StatCard
              title="Online"
              value={<AnimatedCounter value={kpis.online} />}
              icon={<GpsFixedIcon />}
              color="success"
              trend={kpis.total > 0 ? {
                value: Math.round((kpis.online / kpis.total) * 100),
                direction: 'up',
              } : undefined}
              subtitle={kpis.total > 0 ? `${Math.round((kpis.online / kpis.total) * 100)}% da frota` : ''}
            />
            <StatCard
              title="Offline"
              value={<AnimatedCounter value={kpis.offline} />}
              icon={<GpsOffIcon />}
              color="error"
              trend={kpis.total > 0 && kpis.offline > 0 ? {
                value: Math.round((kpis.offline / kpis.total) * 100),
                direction: 'down',
              } : undefined}
              subtitle={kpis.total > 0 ? `${Math.round((kpis.offline / kpis.total) * 100)}% da frota` : ''}
            />
            <StatCard
              title={t('sharedMoving') || 'Movimento'}
              value={<AnimatedCounter value={kpis.moving} />}
              icon={<SpeedIcon />}
              color="info"
              subtitle={kpis.online > 0 ? `${Math.round((kpis.moving / kpis.online) * 100)}% dos online` : ''}
            />
            <StatCard
              title={t('sharedAlerts') || 'Alertas'}
              value={<AnimatedCounter value={kpis.alerts} />}
              icon={<WarningAmberIcon />}
              color="warning"
              onClick={() => navigate('/reports/events')}
              subtitle="Clique para ver"
            />
            <StatCard
              title={t('sharedTotalDistance') || 'Km Total'}
              value={kpis.totalDistanceKm.toLocaleString()}
              icon={<RouteIcon />}
              color="primary"
              subtitle="Odômetro acumulado"
            />
          </StatCardGrid>
        </SlideUp>

        {/* Tabs */}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={t('sharedOverview') || 'Visão Geral'} icon={<LocalShippingIcon />} iconPosition="start" />
          <Tab label={t('sharedDetails') || 'Detalhes'} icon={<TimelineIcon />} iconPosition="start" disabled={!selectedDevice} />
          <Tab label={t('sharedAnalytics') || 'Análises'} icon={<TrendingUpIcon />} iconPosition="start" />
        </Tabs>

        {/* Tab: Overview */}
        {tab === 0 && (
          <>
            {/* Table */}
            <Card className={classes.tableCard}>
              <Box className={classes.tableToolbar}>
                <Box className={classes.tableFilters}>
                  <TextField
                    size="small"
                    placeholder={t('sharedSearch') || 'Buscar veículo...'}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 250 }}
                  />
                  <Chip
                    label={`${t('sharedAll') || 'Todos'} (${kpis.total})`}
                    onClick={() => { setStatusFilter('all'); setPage(0); }}
                    sx={{
                      bgcolor: statusFilter === 'all' ? `${tokens?.accent?.primary || '#00d4aa'}20` : 'transparent',
                      color: statusFilter === 'all' ? (tokens?.accent?.primary || '#00d4aa') : textSecondary,
                      border: `1px solid ${statusFilter === 'all' ? (tokens?.accent?.primary || '#00d4aa') : borderSubtle}`,
                      '& .MuiChip-label': { background: 'transparent' },
                    }}
                  />
                  <Chip
                    label={`Online (${kpis.online})`}
                    onClick={() => { setStatusFilter('online'); setPage(0); }}
                    sx={{
                      bgcolor: statusFilter === 'online' ? `${statusColors.online}20` : 'transparent',
                      color: statusFilter === 'online' ? statusColors.online : textSecondary,
                      border: `1px solid ${statusFilter === 'online' ? statusColors.online : borderSubtle}`,
                      '& .MuiChip-label': { background: 'transparent' },
                    }}
                  />
                  <Chip
                    label={`Offline (${kpis.offline})`}
                    onClick={() => { setStatusFilter('offline'); setPage(0); }}
                    sx={{
                      bgcolor: statusFilter === 'offline' ? `${statusColors.offline}20` : 'transparent',
                      color: statusFilter === 'offline' ? statusColors.offline : textSecondary,
                      border: `1px solid ${statusFilter === 'offline' ? statusColors.offline : borderSubtle}`,
                      '& .MuiChip-label': { background: 'transparent' },
                    }}
                  />
                  <Chip
                    label={`${t('sharedMoving') || 'Movimento'} (${kpis.moving})`}
                    onClick={() => { setStatusFilter('moving'); setPage(0); }}
                    sx={{
                      bgcolor: statusFilter === 'moving' ? `${statusColors.moving}20` : 'transparent',
                      color: statusFilter === 'moving' ? statusColors.moving : textSecondary,
                      border: `1px solid ${statusFilter === 'moving' ? statusColors.moving : borderSubtle}`,
                      '& .MuiChip-label': { background: 'transparent' },
                    }}
                  />
                  {/* Group Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('groupParent') || 'Grupo'}</InputLabel>
                    <Select
                      value={groupFilter}
                      label={t('groupParent') || 'Grupo'}
                      onChange={(e) => { setGroupFilter(e.target.value); setPage(0); }}
                    >
                      <MenuItem value="all">{t('sharedAll') || 'Todos'}</MenuItem>
                      {availableGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {filteredDevices.length} {t('sharedDevices') || 'veículos'}
                </Typography>
              </Box>

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => handleSort('name')}
                        >
                          {t('sharedName') || 'Veículo'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'driverName'}
                          direction={orderBy === 'driverName' ? order : 'asc'}
                          onClick={() => handleSort('driverName')}
                        >
                          {t('sharedDriver') || 'Motorista'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'groupName'}
                          direction={orderBy === 'groupName' ? order : 'asc'}
                          onClick={() => handleSort('groupName')}
                        >
                          {t('groupParent') || 'Grupo'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>{t('positionIgnition') || 'Ignição'}</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'speed'}
                          direction={orderBy === 'speed' ? order : 'asc'}
                          onClick={() => handleSort('speed')}
                        >
                          {t('positionSpeed') || 'Velocidade'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>{t('positionTemperature') || 'Temperatura'}</TableCell>
                      <TableCell>{t('positionBattery') || 'Bateria'}</TableCell>
                      <TableCell>{t('positionAddress') || 'Endereço'}</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'lastUpdate'}
                          direction={orderBy === 'lastUpdate' ? order : 'asc'}
                          onClick={() => handleSort('lastUpdate')}
                        >
                          {t('deviceLastUpdate') || 'Última Atualização'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">{t('sharedActions') || 'Ações'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDevices.map((device) => (
                      <TableRow
                        key={device.id}
                        className={classes.deviceRow}
                        onClick={() => handleDeviceClick(device)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              className={classes.deviceAvatar}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: device.isOnline
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.grey[500], 0.1),
                                color: device.isOnline
                                  ? theme.palette.success.main
                                  : theme.palette.grey[500],
                              }}
                            >
                              {getCategoryIcon(device.category)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{device.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {device.uniqueId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Driver Column */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {device.driverName}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Group Column */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FolderIcon fontSize="small" color="action" />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {device.groupName}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Status Column */}
                        <TableCell>
                          <Chip
                            size="small"
                            label={device.isOnline ? (device.isMoving ? t('deviceStatusMoving') || 'Movimento' : t('deviceStatusStopped') || 'Parado') : 'Offline'}
                            className={classes.statusChip}
                            sx={{
                              bgcolor: device.isOnline
                                ? (device.isMoving ? `${statusColors.moving}20` : `${statusColors.online}20`)
                                : `${statusColors.offline}20`,
                              color: device.isOnline
                                ? (device.isMoving ? statusColors.moving : statusColors.online)
                                : statusColors.offline,
                              border: `1px solid ${device.isOnline
                                ? (device.isMoving ? statusColors.moving : statusColors.online)
                                : statusColors.offline}`,
                              '& .MuiChip-label': { background: 'transparent' },
                            }}
                          />
                        </TableCell>
                        {/* Ignition Column */}
                        <TableCell>
                          {device.ignition !== undefined ? (
                            <Chip
                              size="small"
                              icon={device.ignition ? <PowerIcon /> : <PowerOffIcon />}
                              label={device.ignition ? (t('positionIgnitionOn') || 'Ligada') : (t('positionIgnitionOff') || 'Desligada')}
                              sx={{
                                bgcolor: device.ignition ? `${statusColors.online}20` : 'transparent',
                                color: device.ignition ? statusColors.online : textSecondary,
                                border: `1px solid ${device.ignition ? statusColors.online : borderSubtle}`,
                                '& .MuiChip-icon': { color: 'inherit' },
                                '& .MuiChip-label': { background: 'transparent' },
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: textSecondary, background: 'transparent' }}>-</Typography>
                          )}
                        </TableCell>
                        {/* Speed Column */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SpeedIcon fontSize="small" color="action" />
                            <Typography variant="body2">{formatSpeed(device.speed)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {device.temperature !== undefined ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ThermostatIcon fontSize="small" color={device.temperature > 8 ? 'error' : 'success'} />
                              <Typography variant="body2">{device.temperature?.toFixed(1)}°C</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {device.battery !== undefined ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {device.battery < 20 ? (
                                <BatteryAlertIcon fontSize="small" color="error" />
                              ) : (
                                <BatteryFullIcon fontSize="small" color="success" />
                              )}
                              <Typography variant="body2">{device.battery}%</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                            {device.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(device.lastUpdate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, device)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={filteredDevices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('sharedRowsPerPage') || 'Linhas por página:'}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Card>

            {/* Charts Grid - Premium */}
            <SlideUp delay={200}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <DashboardWidget title={t('sharedStatusDistribution') || 'Distribuição por Status'}>
                    <StatusDistribution
                      data={statusDistributionData}
                      variant="bar"
                      showLegend
                      showPercentage
                    />
                  </DashboardWidget>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <DashboardWidget title={t('sharedByCategory') || 'Veículos por Categoria'}>
                    <PremiumBarChart
                      data={categoryDistribution}
                      xKey="name"
                      bars={[{ dataKey: 'count', name: 'Quantidade' }]}
                      height={250}
                      showLegend={false}
                    />
                  </DashboardWidget>
                </Grid>
              </Grid>
            </SlideUp>
          </>
        )}

        {/* Tab: Details */}
        {tab === 1 && selectedDevice && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: selectedDevice.isOnline
                          ? alpha(theme.palette.success.main, 0.1)
                          : alpha(theme.palette.grey[500], 0.1),
                        color: selectedDevice.isOnline
                          ? theme.palette.success.main
                          : theme.palette.grey[500],
                      }}
                    >
                      {getCategoryIcon(selectedDevice.category)}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={600}>
                        {selectedDevice.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={selectedDevice.isOnline ? 'Online' : 'Offline'}
                        color={selectedDevice.isOnline ? 'success' : 'error'}
                      />
                    </Box>
                  </Box>

                  {/* Speed Gauge with Metrics */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 2 }}>
                    {/* Left Metric - Temperature or Battery */}
                    <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                      {selectedDevice.temperature !== undefined ? (
                        <>
                          <ThermostatIcon
                            sx={{ fontSize: 28 }}
                            color={selectedDevice.temperature > 8 ? 'error' : 'success'}
                          />
                          <Typography variant="h6" fontWeight={700}>
                            {selectedDevice.temperature?.toFixed(1)}°C
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('positionTemperature') || 'Temp'}
                          </Typography>
                        </>
                      ) : selectedDevice.battery !== undefined ? (
                        <>
                          {selectedDevice.battery < 20 ? (
                            <BatteryAlertIcon sx={{ fontSize: 28 }} color="error" />
                          ) : (
                            <BatteryFullIcon sx={{ fontSize: 28 }} color="success" />
                          )}
                          <Typography variant="h6" fontWeight={700}>
                            {selectedDevice.battery}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('positionBattery') || 'Bateria'}
                          </Typography>
                        </>
                      ) : null}
                    </Box>

                    {/* Center - Speed Gauge */}
                    <SpeedGauge value={Math.round((selectedDevice.speed || 0) * 1.852)} />

                    {/* Right Metric - Battery (if temperature exists) */}
                    <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                      {selectedDevice.temperature !== undefined && selectedDevice.battery !== undefined ? (
                        <>
                          {selectedDevice.battery < 20 ? (
                            <BatteryAlertIcon sx={{ fontSize: 28 }} color="error" />
                          ) : (
                            <BatteryFullIcon sx={{ fontSize: 28 }} color="success" />
                          )}
                          <Typography variant="h6" fontWeight={700}>
                            {selectedDevice.battery}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('positionBattery') || 'Bateria'}
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PlaceIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('positionAddress') || 'Endereço'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{selectedDevice.address}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('deviceLastUpdate') || 'Última Atualização'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{formatDate(selectedDevice.lastUpdate)}</Typography>
                  </Box>

                  {/* Driver */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('sharedDriver') || 'Motorista'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{selectedDevice.driverName}</Typography>
                  </Box>

                  {/* Group */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FolderIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('groupParent') || 'Grupo'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{selectedDevice.groupName}</Typography>
                  </Box>

                  {/* Ignition Status */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {selectedDevice.ignition ? <PowerIcon fontSize="small" color="success" /> : <PowerOffIcon fontSize="small" color="action" />}
                      <Typography variant="caption" color="text.secondary">
                        {t('positionIgnition') || 'Ignição'}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {selectedDevice.ignition === true ? (t('positionIgnitionOn') || 'Ligada') :
                       selectedDevice.ignition === false ? (t('positionIgnitionOff') || 'Desligada') : '-'}
                    </Typography>
                  </Box>

                  {/* Total Distance */}
                  {selectedDevice.totalDistance && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <RouteIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {t('sharedTotalDistance') || 'Km Total'}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {Math.round(selectedDevice.totalDistance / 1000).toLocaleString()} km
                      </Typography>
                    </Box>
                  )}

                  {/* Mini Map */}
                  {selectedDevice.latitude && selectedDevice.longitude && (
                    <Box className={classes.mapContainer}>
                      <iframe
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps?q=${selectedDevice.latitude},${selectedDevice.longitude}&z=15&output=embed`}
                        allowFullScreen
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<MapIcon />}
                      onClick={() => {
                        const pos = positions[selectedDevice.id];
                        const isMoving = pos?.attributes?.motion === true;
                        navigate(`/?deviceId=${selectedDevice.id}${isMoving ? '&follow=true' : ''}`);
                      }}
                      fullWidth
                    >
                      {t('sharedShowOnMap') || 'Ver no Mapa'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sharedSpeedHistory') || 'Histórico de Velocidade'} (6h)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {loadingHistory ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                      </Box>
                    ) : historicalData.length > 0 ? (
                      <PremiumLineChart
                        data={historicalData}
                        xKey="time"
                        lines={[
                          { dataKey: 'speed', name: 'Velocidade', color: tokens?.accent?.primary },
                        ]}
                        height={280}
                        yAxisUnit=" km/h"
                        tooltipFormatter={(value) => `${value} km/h`}
                        showArea
                      />
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography color="text.secondary" sx={{ background: 'transparent' }}>
                          {t('sharedNoData') || 'Sem dados de histórico'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Recent Events - Premium Activity Feed */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                      <EventIcon sx={{ mr: 1 }} />
                      {t('reportEvents') || 'Eventos Recentes'} (24h)
                    </Typography>
                    <Chip
                      size="small"
                      label={deviceEvents.length}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ maxHeight: 350, overflow: 'hidden' }}>
                    <PremiumActivityFeed
                      events={deviceEvents}
                      loading={loadingEvents}
                      onEventClick={handleEventClick}
                      groupByDay
                      showDevice={false}
                      maxHeight={350}
                      emptyMessage={t('sharedNoEvents') || 'Nenhum evento nas últimas 24h'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab: Analytics */}
        {tab === 2 && (
          <Grid container spacing={2}>
            {/* Speed Distribution */}
            <Grid size={{ xs: 12, md: 6 }}>
              <DashboardWidget title={t('sharedSpeedDistribution') || 'Distribuição de Velocidade'}>
                <PremiumBarChart
                  data={speedDistribution}
                  xKey="range"
                  bars={[{ dataKey: 'count', name: 'Veículos', color: statusColors.moving }]}
                  height={250}
                  showLegend={false}
                />
              </DashboardWidget>
            </Grid>

            {/* Summary Metrics */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card className={classes.chartCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sharedSummary') || 'Resumo da Frota'}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={6}>
                      <Paper className={classes.metricCard} elevation={0} sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {Math.round((kpis.online / kpis.total) * 100) || 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedOnlineRate') || 'Taxa Online'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid size={6}>
                      <Paper className={classes.metricCard} elevation={0} sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                        <Typography variant="h4" fontWeight={700} color="info.main">
                          {kpis.avgSpeed}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedAvgSpeed') || 'Vel. Média (km/h)'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid size={6}>
                      <Paper className={classes.metricCard} elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {kpis.moving}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedMovingNow') || 'Em Movimento'}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid size={6}>
                      <Paper className={classes.metricCard} elevation={0} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {kpis.alerts}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedActiveAlerts') || 'Alertas Ativos'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Category Breakdown */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('sharedCategoryBreakdown') || 'Detalhamento por Categoria'}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('sharedCategory') || 'Categoria'}</TableCell>
                          <TableCell align="right">{t('sharedTotal') || 'Total'}</TableCell>
                          <TableCell align="right">Online</TableCell>
                          <TableCell align="right">Offline</TableCell>
                          <TableCell align="right">{t('sharedMoving') || 'Movimento'}</TableCell>
                          <TableCell align="right">%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryDistribution.map((cat) => {
                          const categoryDevices = devices.filter(d => (d.category || 'outros') === cat.name);
                          const catOnline = categoryDevices.filter(d => {
                            const pos = positions[d.id];
                            const lastTime = pos ? new Date(pos.fixTime).getTime() : 0;
                            return Date.now() - lastTime < 5 * 60 * 1000;
                          }).length;
                          const catMoving = categoryDevices.filter(d => {
                            const pos = positions[d.id];
                            return pos?.speed > 0;
                          }).length;

                          return (
                            <TableRow key={cat.name}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getCategoryIcon(cat.name)}
                                  <Typography>{cat.name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{cat.count}</TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={catOnline} color="success" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={cat.count - catOnline} color="error" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={catMoving} color="info" />
                              </TableCell>
                              <TableCell align="right">
                                {Math.round((cat.count / kpis.total) * 100)}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Context Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => {
            handleMenuClose();
            const pos = positions[menuDevice?.id];
            const isMoving = pos?.attributes?.motion === true;
            navigate(`/?deviceId=${menuDevice?.id}${isMoving ? '&follow=true' : ''}`);
          }}>
            <MapIcon sx={{ mr: 1 }} fontSize="small" /> {t('sharedShowOnMap') || 'Ver no Mapa'}
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate(`/replay?deviceId=${menuDevice?.id}`); }}>
            <RouteIcon sx={{ mr: 1 }} fontSize="small" /> {t('reportReplay') || 'Replay'}
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate(`/settings/device/${menuDevice?.id}`); }}>
            <TimelineIcon sx={{ mr: 1 }} fontSize="small" /> {t('sharedEdit') || 'Editar'}
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <NotificationsActiveIcon sx={{ mr: 1 }} fontSize="small" /> {t('sharedNotifications') || 'Alertas'}
          </MenuItem>
        </Menu>
      </Box>
    </PageLayout>
  );
};

export default FleetDashboardPage;
